import React from "react";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { ExternalLink } from "lucide-react";
import {
  TraceTimeline,
  TraceRail,
  TraceLine,
  TraceDot,
  TraceCard,
  DetailBox,
  SmallGhostBtn,
  ImagesContainer,
  TrackingImage,
  DocumentsContainer,
  DocumentCard,
  DocumentIcon,
  DocumentInfo,
  DocumentName,
  DocumentHint,
  DocumentArrow,
  MapBox,
  MapFallback,
} from "./TraceabilityStyles";

const shortHash = (h) => {
  if (!h) return '—';
  if (h.length <= 5) return h;
  // Show first 3 chars, ellipsis, and last 2 chars: "123...ed"
  return `${h.slice(0, 3)}...${h.slice(-2)}`;
};

const TrackingEventCard = ({
  event,
  isLast,
  isExpanded,
  isProductOwner,
  isMapLoaded,
  txBase,
  onToggle,
  onViewHistory,
  onAddDescription,
  onAddCertification,
}) => {
  return (
    <TraceTimeline>
      <TraceRail>
        {!isLast && <TraceLine />}
        <TraceDot status={event.status} />
      </TraceRail>

      <TraceCard>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div>
            <strong style={{ color: '#e5e7eb', fontSize: 15 }}>{event.title}</strong>
            <div style={{ fontSize: 12, opacity: 0.75, marginTop: 6 }}>
              {new Date(event.date).toLocaleString()} · {event.location}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {event.tx ? (
              <a
                href={`${txBase}${encodeURIComponent(event.tx)}`}
                target="_blank"
                rel="noreferrer"
                style={{
                  fontSize: 12,
                  opacity: 0.85,
                  border: '1px solid #1f2937',
                  borderRadius: 999,
                  padding: '4px 10px',
                  background: 'rgba(17,21,34,0.55)',
                  color: '#00f7d2',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontFamily: 'monospace'
                }}
              >
                TX: {shortHash(String(event.tx))}
                <ExternalLink size={12} style={{ opacity: 0.8 }} />
              </a>
            ) : null}
            <SmallGhostBtn onClick={() => onViewHistory(event.rawData._id)} style={{ borderColor: 'rgba(92,233,255,0.35)', color: '#5ce9ff' }}>
              View History
            </SmallGhostBtn>
            {isProductOwner && (
              <>
                <SmallGhostBtn onClick={() => onAddDescription(event.rawData._id)}>
                  Add Description
                </SmallGhostBtn>
                <SmallGhostBtn onClick={() => onAddCertification(event.rawData._id)} style={{ borderColor: 'rgba(34,197,94,0.35)', color: '#22c55e' }}>
                  Add Certification
                </SmallGhostBtn>
              </>
            )}
            <SmallGhostBtn onClick={() => onToggle(event.id)}>
              {isExpanded ? 'Hide' : 'Details'}
            </SmallGhostBtn>
          </div>
        </div>

        {isExpanded && (
          <DetailBox>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>Phase</div>
                <div style={{ fontWeight: 900, color: '#5ce9ff' }}>{event.phase || 'Transfer'}</div>
              </div>
              <div>
                <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>Status</div>
                <div style={{ fontWeight: 900, color: event.status === 'ok' ? '#00f7d2' : event.status === 'pending' ? '#facc15' : '#5ce9ff' }}>
                  {String(event.status).toUpperCase()}
                </div>
              </div>
            </div>

            {event.batch_number && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>Batch Number</div>
                <div style={{ fontWeight: 700, color: '#e5e7eb' }}>{event.batch_number}</div>
              </div>
            )}

            <div style={{ marginTop: 10 }}>{event.detail || '—'}</div>

            {event.tx ? (
              <div style={{ marginTop: 10, fontSize: 12, opacity: 0.85, display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span>Full TX:</span>
                <a
                  href={`${txBase}${encodeURIComponent(event.tx)}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    color: '#00f7d2',
                    fontWeight: 900,
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontFamily: 'monospace'
                  }}
                >
                  {shortHash(String(event.tx))}
                  <ExternalLink size={14} style={{ opacity: 0.8 }} />
                </a>
              </div>
            ) : null}

            {event.images && event.images.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>Images</div>
                <ImagesContainer>
                  {event.images.map((image, imgIndex) => (
                    <TrackingImage
                      key={imgIndex}
                      src={image}
                      alt={`Tracking image ${imgIndex + 1}`}
                      onClick={() => window.open(image, '_blank')}
                    />
                  ))}
                </ImagesContainer>
              </div>
            )}

            {event.documents && event.documents.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>Documents</div>
                <DocumentsContainer>
                  {event.documents.map((document, docIndex) => (
                    <DocumentCard
                      key={docIndex}
                      onClick={() => window.open(document, '_blank')}
                    >
                      <DocumentIcon>📄</DocumentIcon>
                      <DocumentInfo>
                        <DocumentName>
                          {`Document ${docIndex + 1}`}
                        </DocumentName>
                        <DocumentHint>
                          Click to open
                        </DocumentHint>
                      </DocumentInfo>
                      <DocumentArrow>→</DocumentArrow>
                    </DocumentCard>
                  ))}
                </DocumentsContainer>
              </div>
            )}

            <div style={{ marginTop: 10, fontSize: 12, opacity: 0.75 }}>
              📍 Location: <strong style={{ color: '#e5e7eb' }}>{event.location || '—'}</strong>
            </div>

            {event.coordinates && isMapLoaded ? (
              <MapBox>
                <GoogleMap
                  mapContainerStyle={{ width: '100%', height: '100%' }}
                  center={event.coordinates}
                  zoom={15}
                  options={{
                    disableDefaultUI: false,
                    zoomControl: true,
                    streetViewControl: false,
                    mapTypeControl: false,
                  }}
                >
                  <Marker position={event.coordinates} />
                </GoogleMap>
              </MapBox>
            ) : (
              <MapBox>
                <MapFallback>No location set for this event</MapFallback>
              </MapBox>
            )}
          </DetailBox>
        )}
      </TraceCard>
    </TraceTimeline>
  );
};

export default TrackingEventCard;
